import { pb } from '@/lib/pocketbase';
import { supabase } from '@/integrations/supabase/client';

// VAPID public key for web push notifications - MUST match the backend VAPID_PUBLIC_KEY secret
// This is a PUBLIC key, safe to expose in frontend code
const VAPID_PUBLIC_KEY = 'BDaf5F9Fh14IvgehvnzKxWbgoUVOkrOeY5WcynINpH5wIsjymIr4J-trsYWX9jGB3YvOcqVxqaR46ik_sZiNXFA';

// Debug log to verify VAPID key is loaded
console.log('🔑 [VAPID] Public key loaded:', VAPID_PUBLIC_KEY ? `Yes (length: ${VAPID_PUBLIC_KEY.length}, starts with: ${VAPID_PUBLIC_KEY.substring(0, 10)}...)` : 'NO - MISSING!');

interface PushSubscriptionData {
  userId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent: string;
}

interface WebPushNotification {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

export const webPushService = {
  // Check if running as iOS PWA (standalone mode)
  isIOSPWA: (): boolean => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = (window.navigator as any).standalone === true || 
                         window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && isStandalone;
  },

  // Check iOS version
  getIOSVersion: (): number | null => {
    const match = navigator.userAgent.match(/OS (\d+)_/);
    return match ? parseInt(match[1], 10) : null;
  },

  // Check if web push is supported
  isSupported: (): boolean => {
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    
    // Check iOS specific requirements
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const iosVersion = webPushService.getIOSVersion();
    const isIOSPWA = webPushService.isIOSPWA();
    
    console.log('Push notification support check:', {
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      isSecure,
      isIOS,
      iosVersion,
      isIOSPWA,
      isStandalone: (window.navigator as any).standalone,
      displayMode: window.matchMedia('(display-mode: standalone)').matches,
      userAgent: navigator.userAgent
    });
    
    // iOS requires 16.4+ AND must be running as PWA (standalone mode)
    if (isIOS) {
      if (!isIOSPWA) {
        console.log('iOS detected but not running as PWA - push not supported');
        return false;
      }
      if (iosVersion && iosVersion < 16) {
        console.log('iOS version too old for push notifications');
        return false;
      }
    }
    
    return hasServiceWorker && hasPushManager && hasNotification && isSecure;
  },

  // Check notification permission
  getPermission: (): NotificationPermission => {
    return Notification.permission;
  },

  // Request notification permission
  requestPermission: async (): Promise<NotificationPermission> => {
    if (!webPushService.isSupported()) {
      throw new Error('Web Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  },

  // Register service worker
  registerServiceWorker: async (): Promise<ServiceWorkerRegistration> => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  },

  // Subscribe to push notifications
  subscribe: async (userId: string): Promise<PushSubscription | null> => {
    try {
      // CRITICAL: Check VAPID key first
      if (!VAPID_PUBLIC_KEY) {
        console.error('❌ [VAPID] VITE_VAPID_PUBLIC_KEY environment variable is NOT SET!');
        console.error('❌ [VAPID] Please set VITE_VAPID_PUBLIC_KEY in secrets to match VAPID_PUBLIC_KEY');
        throw new Error('Push notifications not configured - missing VITE_VAPID_PUBLIC_KEY environment variable. Please contact support.');
      }
      
      console.log('🔑 [VAPID] Using public key:', VAPID_PUBLIC_KEY.substring(0, 20) + '...');
      
      // Check permission
      if (webPushService.getPermission() !== 'granted') {
        const permission = await webPushService.requestPermission();
        if (permission !== 'granted') {
          console.log('Push notifications permission denied');
          return null;
        }
      }

      // Register service worker
      const registration = await webPushService.registerServiceWorker();
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Check if already subscribed - unsubscribe first to ensure fresh subscription with correct VAPID key
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('📱 [PUSH] Found existing subscription, checking if VAPID key matches...');
        // Always unsubscribe and create fresh subscription to ensure VAPID key is correct
        await existingSubscription.unsubscribe();
        console.log('📱 [PUSH] Unsubscribed old subscription to create fresh one with current VAPID key');
      }

      // Create new subscription with current VAPID key
      console.log('📱 [PUSH] Creating new subscription with VAPID key...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: webPushService.urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource
      });

      console.log('✅ [PUSH] New push subscription created:', subscription.endpoint);
      console.log('📱 [PUSH] Subscription keys:', {
        p256dh: subscription.toJSON().keys?.p256dh?.substring(0, 20) + '...',
        auth: subscription.toJSON().keys?.auth?.substring(0, 10) + '...'
      });
      
      // Save subscription to database
      await webPushService.savePushSubscription(userId, subscription);
      
      return subscription;
    } catch (error) {
      console.error('❌ [PUSH] Error subscribing to push notifications:', error);
      throw error;
    }
  },

  // Save push subscription to database
  savePushSubscription: async (userId: string, subscription: PushSubscription): Promise<void> => {
    try {
      console.log('📱 [PUSH] Saving subscription for user:', userId);
      console.log('📱 [PUSH] Subscription endpoint:', subscription.endpoint);
      
      const subscriptionData: PushSubscriptionData = {
        userId,
        endpoint: subscription.endpoint,
        p256dh: subscription.toJSON().keys?.p256dh || '',
        auth: subscription.toJSON().keys?.auth || '',
        userAgent: navigator.userAgent
      };

      console.log('📱 [PUSH] Subscription data to save:', JSON.stringify(subscriptionData, null, 2));

      // IMPORTANT: Delete ALL existing subscriptions for this user first
      // This ensures old subscriptions with outdated VAPID keys are removed
      console.log('🗑️ [PUSH] Deleting ALL old subscriptions for user...');
      try {
        const allExisting = await pb.collection('push_notifications').getList(1, 100, {
          filter: `userId = "${userId}"`
        });
        
        console.log(`🗑️ [PUSH] Found ${allExisting.items.length} old subscription(s) to delete`);
        
        for (const record of allExisting.items) {
          await pb.collection('push_notifications').delete(record.id);
          console.log(`🗑️ [PUSH] Deleted old subscription: ${record.id}`);
        }
      } catch (deleteError) {
        console.warn('⚠️ [PUSH] Error deleting old subscriptions:', deleteError);
      }

      // Create fresh subscription
      console.log('📱 [PUSH] Creating fresh subscription...');
      const created = await pb.collection('push_notifications').create(subscriptionData);
      console.log('✅ [PUSH] Saved fresh push subscription:', created.id);
    } catch (error: any) {
      console.error('❌ [PUSH] Error saving push subscription:', error);
      console.error('❌ [PUSH] Error details:', error?.data || error?.message || error);
      throw error;
    }
  },

  // Unsubscribe from push notifications
  unsubscribe: async (userId: string): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      // Unsubscribe from push manager
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');

      // Remove from database
      const existing = await pb.collection('push_notifications').getList(1, 10, {
        filter: `userId = "${userId}"`
      });

      for (const record of existing.items) {
        await pb.collection('push_notifications').delete(record.id);
      }
      
      console.log('Removed push subscriptions from database');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  },

  // Send test notification
  sendTestNotification: async (title: string, body: string): Promise<void> => {
    if (webPushService.getPermission() !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    // Send local notification for testing
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'test-notification'
    });
  },

  // Utility function to convert VAPID key
  urlBase64ToUint8Array: (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  },

  // Check if user is subscribed
  isSubscribed: async (): Promise<boolean> => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return false;

      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }
};

// Enhanced notification service with web push integration
export const enhancedNotificationService = {
  // Send notification with web push support
  sendNotificationWithPush: async (
    userId: string,
    notification: WebPushNotification,
    saveToDatabase: boolean = true
  ) => {
    console.log('🔔 [ENHANCED] sendNotificationWithPush called:', { userId, saveToDatabase, notification });
    try {
      // Save to database if requested (existing functionality)
      if (saveToDatabase) {
        console.log('🔔 [ENHANCED] Creating notification record in PocketBase...');
        const created = await pb.collection('notifications').create({
          userId,
          type: notification.data?.type || 'general',
          title: notification.title,
          message: notification.body,
          isRead: false,
          createdAt: new Date().toISOString(),
          data: notification.data
        });
        console.log('✅ [ENHANCED] Notification record created:', created.id);
      }

      // Get user's push subscriptions from PocketBase
      try {
        console.log('📲 [PUSH] Looking for push subscriptions for user:', userId);
        
        // First, let's see ALL records to debug
        const allSubs = await pb.collection('push_notifications').getList(1, 50);
        console.log('📲 [PUSH] All subscriptions in collection:', allSubs.items.map(s => ({ id: s.id, userId: s.userId })));
        
        // Now try the filter
        const subscriptions = await pb.collection('push_notifications').getList(1, 10, {
          filter: `userId = "${userId}"`
        });

        console.log('📲 [PUSH] Found subscriptions with filter:', subscriptions.items.length);
        console.log('📲 [PUSH] Found subscriptions:', subscriptions.items.length);
        
        if (subscriptions.items.length > 0) {
          console.log('📲 [PUSH] Sending push to', subscriptions.items.length, 'device(s)...');
          
          // Separate web push subscriptions from native FCM tokens
          const webPushSubs = subscriptions.items.filter(sub => !sub.fcmToken || !sub.endpoint?.startsWith('fcm:'));
          const fcmSubs = subscriptions.items.filter(sub => sub.fcmToken && sub.endpoint?.startsWith('fcm:'));
          
          console.log('📲 [PUSH] Web Push subscriptions:', webPushSubs.length);
          console.log('📲 [PUSH] FCM tokens (native):', fcmSubs.length);
          
          // Send push notification via edge function
          const { data, error } = await supabase.functions.invoke('send-push-notification', {
            body: {
              // Web push subscriptions
              subscriptions: webPushSubs.map(sub => ({
                endpoint: sub.endpoint,
                p256dh: sub.p256dh,
                auth: sub.auth
              })),
              // Native FCM tokens
              fcmTokens: fcmSubs.map(sub => ({
                token: sub.fcmToken,
                platform: sub.platform === 'ios' ? 'ios' : 'android'
              })),
              notification: {
                title: notification.title,
                body: notification.body,
                icon: notification.icon || '/favicon.ico',
                badge: notification.badge || '/favicon.ico',
                tag: notification.tag,
                data: notification.data
              }
            }
          });

          if (error) {
            console.error('❌ [PUSH] Error sending push via edge function:', error);
          } else {
            console.log('✅ [PUSH] Push notification sent successfully:', data);
          }
        } else {
          console.log('⚠️ [PUSH] No push subscriptions found for user:', userId, '- user needs to enable push notifications first');
        }
      } catch (pushError) {
        console.error('❌ [PUSH] Error fetching push subscriptions or sending push:', pushError);
        // Don't throw - push notification failure shouldn't break the notification flow
      }
    } catch (error) {
      console.error('Error sending enhanced notification:', error);
      throw error;
    }
  },

  // Send push notification to multiple users
  sendPushToUsers: async (userIds: string[], notification: WebPushNotification) => {
    try {
      // Get all subscriptions for the users
      const filter = userIds.map(id => `userId = "${id}"`).join(' || ');
      const subscriptions = await pb.collection('push_notifications').getList(1, 100, {
        filter
      });

      if (subscriptions.items.length === 0) {
        console.log('No push subscriptions found for users');
        return;
      }

      // Separate web push subscriptions from native FCM tokens
      const webPushSubs = subscriptions.items.filter(sub => !sub.fcmToken || !sub.endpoint?.startsWith('fcm:'));
      const fcmSubs = subscriptions.items.filter(sub => sub.fcmToken && sub.endpoint?.startsWith('fcm:'));
      
      // Send via edge function
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          subscriptions: webPushSubs.map(sub => ({
            endpoint: sub.endpoint,
            p256dh: sub.p256dh,
            auth: sub.auth
          })),
          fcmTokens: fcmSubs.map(sub => ({
            token: sub.fcmToken,
            platform: sub.platform === 'ios' ? 'ios' : 'android'
          })),
          notification: {
            title: notification.title,
            body: notification.body,
            icon: notification.icon || '/favicon.ico',
            badge: notification.badge || '/favicon.ico',
            tag: notification.tag,
            data: notification.data
          }
        }
      });

      if (error) {
        console.error('Error sending push notifications:', error);
      } else {
        console.log('Push notifications sent to multiple users:', data);
      }
    } catch (error) {
      console.error('Error in sendPushToUsers:', error);
    }
  }
};
